import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobalStore } from '../../store/global-store/global-store';
import { disconnectGoogle, fetchGoogleFiles, fetchGoogleStatus } from './google-integrations-client';
import type { GoogleDriveFile, UseGoogleStatusResult } from './google-integrations-client.type';

const GOOGLE_STATUS_QUERY_KEY = 'google-integration-status';
const GOOGLE_FILES_QUERY_KEY = 'google-integration-files';

/**
 * Hook to fetch the current user's Google connection status for the current org.
 *
 * Mirrors ``useSlackStatus``: overrides the app's global ``staleTime: 5min`` /
 * ``refetchOnMount: false`` defaults because the post-OAuth redirect lands the
 * user back on the integrations page expecting an immediate state change, so we
 * always refetch on mount and never serve stale cache.
 *
 * @param {boolean} enabled - When false, the query is paused (e.g. flag off).
 * @returns {UseGoogleStatusResult} Status object plus the underlying React Query result.
 */
function useGoogleStatus(enabled = true): UseGoogleStatusResult {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [GOOGLE_STATUS_QUERY_KEY, organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return fetchGoogleStatus(organizationId);
    },
    enabled: !!organizationId && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  return { data: query.data, query };
}

/**
 * Hook to disconnect the current user's Google account from the org.
 *
 * Invalidates the status query and the ``['user']`` query on success so the page
 * and the user's connected-integrations state reflect the change immediately.
 *
 * @returns {UseMutationResult<void, Error, void>} Mutation result.
 */
function useDisconnectGoogle(): UseMutationResult<void, Error, void> {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!organizationId) {
        throw new Error('Organization ID is required to disconnect Google account');
      }
      return disconnectGoogle(organizationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [GOOGLE_STATUS_QUERY_KEY, organizationId] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
}

/**
 * Hook to list the Google Drive files Flora can access for the current org.
 *
 * Powers the flora-agent Documents page's Google Drive section. Callers gate
 * `enabled` on the feature flag and connection state so the query only runs
 * when a listing is possible.
 *
 * @param {boolean} enabled - When false, the query is paused (flag off / not connected).
 * @returns {UseQueryResult<GoogleDriveFile[]>} The React Query result for the file list.
 */
function useGoogleFiles(enabled = true): UseQueryResult<GoogleDriveFile[]> {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  return useQuery({
    queryKey: [GOOGLE_FILES_QUERY_KEY, organizationId],
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return fetchGoogleFiles(organizationId);
    },
    enabled: !!organizationId && enabled,
    // A 409 (not connected / reconnect required) is a terminal, meaningful
    // state — don't retry it into a prolonged spinner.
    retry: false,
  });
}

export { GOOGLE_FILES_QUERY_KEY, useDisconnectGoogle, useGoogleFiles, useGoogleStatus };
